import { Camera, Link2, CheckCircle2, CalendarDays, Share, Crosshair } from "lucide-react"
import type { LinaTaskAction, SuggestionAction } from "@shared/dashboard"

// Exactly one optional task per week, with a reason. The only orange action on the page.
// Skipping is allowed without a follow-up question so no counter builds up.

export type WeeklyTaskAction = SuggestionAction | LinaTaskAction

/** Hotel-Edition: hotels do not know Emma (Mike 2026-10-10). Only the reasons that name her differ. */
const HOTEL_REASONS: Partial<Record<WeeklyTaskAction, string>> = {
  "open-strategy": "Ohne Strategie schreibt Lina ohne Säulen.",
  "upload-photo": "Ihr Team baut daraus die nächsten Beiträge. Ohne Fotos nimmt es eine Grafik.",
}

export function taskReason(action: WeeklyTaskAction, hotel: boolean): string {
  return (hotel && HOTEL_REASONS[action]) || TASK_COPY[action].reason
}

const TASK_COPY: Record<WeeklyTaskAction, { title: string; reason: string; icon: typeof Camera }> = {
  "open-strategy": {
    title: "Beantworten Sie die drei Strategie-Fragen",
    reason: "Ohne Strategie schreibt Emma ohne Säulen. Drei Antworten reichen, den Rest baut Lina.",
    icon: Crosshair,
  },
  "upload-photo": {
    title: "Laden Sie drei neue Fotos hoch",
    reason: "Emma baut daraus die nächsten Beiträge. Ohne Fotos nimmt sie eine Grafik.",
    icon: Camera,
  },
  "add-url": {
    title: "Teilen Sie einen Link",
    reason: "Ein Artikel oder Video reicht, Ihr Team macht daraus fertige Beiträge.",
    icon: Link2,
  },
  "review-posts": {
    title: "Prüfen Sie die wartenden Beiträge",
    reason: "Erst nach Ihrer Freigabe gehen sie raus.",
    icon: CheckCircle2,
  },
  "open-wochenplan": {
    title: "Werfen Sie einen Blick in den Wochenplan",
    reason: "Dort sehen Sie, was als Nächstes kommt.",
    icon: CalendarDays,
  },
  "connect-platform": {
    title: "Verbinden Sie einen Kanal",
    reason: "Ohne verbundenen Kanal kann Ihr Team nichts veröffentlichen.",
    icon: Share,
  },
}

interface WeeklyTaskCardProps {
  action: WeeklyTaskAction
  ctaLabel: string
  /** Lina's stored task carries its own wording; the suggestion fallback uses TASK_COPY */
  title?: string
  reason?: string
  onCta: () => void
  onSkip: () => void
  skipPending?: boolean
  hotel?: boolean
}

export function WeeklyTaskCard({ action, ctaLabel, title, reason, onCta, onSkip, skipPending, hotel = false }: WeeklyTaskCardProps) {
  const copy = { ...TASK_COPY[action], reason: taskReason(action, hotel), ...(title ? { title } : {}), ...(reason ? { reason } : {}) }
  const Icon = copy.icon
  return (
    <section
      className="flex flex-wrap items-center gap-3.5 rounded-xl border border-dashed border-border px-4 py-3.5"
      data-testid="cockpit-task"
    >
      <span className="grid h-10 w-10 flex-none place-items-center rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-950/60 dark:text-orange-400">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-semibold text-foreground">{copy.title}</p>
        <p className="text-[12.5px] text-muted-foreground">{copy.reason}</p>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <button
          type="button"
          onClick={onSkip}
          disabled={skipPending}
          className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-60"
          data-testid="button-task-skip"
        >
          Diese Woche überspringen
        </button>
        <button
          type="button"
          onClick={onCta}
          className="whitespace-nowrap rounded-full bg-orange-500 px-4 py-2 text-[13px] font-semibold text-white shadow-[0_6px_16px_-6px_rgba(249,115,22,0.6)] transition-colors hover:bg-orange-600"
          data-testid="button-task-cta"
        >
          {ctaLabel}
        </button>
      </div>
    </section>
  )
}
